import { useObservable } from '@streem/sdk-react';
import { createContextFactory } from '@streem/toolbox';
import { PropsWithChildren, useCallback, useEffect, useMemo } from 'react';
import { useDetailSession } from './detail_session_hooks';
import { useGlobalStore } from './use_global_context';
import { ToastTypes } from '@streem/ui-react';
import { FailedToSendNoteToastContent } from '../components/call_details/failed_to_send_note_toast_content';
import appLogger from '../util/logging/app_logger';
import { reaction } from 'mobx';
import { DetailSession } from '@streem/sdk-core';

export const handleCallAnswered = async (
    roomSid: string,
    session: DetailSession,
    saveMarkdown: (text: string) => Promise<void>,
): Promise<void> => {
    // Read from the stable controller reference, not stale closure
    const needsSaving = session.note.isNeedingSave;
    const text = session.note.currentInputText;

    if (needsSaving) {
        try {
            await saveMarkdown(text);
            appLogger.info('Saved a note before answering a new call for room', roomSid);
        } catch (error) {
            appLogger.error('Failed to save note before answering call:', error);
        }
    }
};

export interface NotesContext {
    roomNoteCharLimit: number;
    currentlySavedMarkdown: string;
    currentInputText: string;
    saveMarkdown: (markdown: string) => Promise<void>;
    sendNote: (markdown: string) => Promise<void>;
    loadedYn: boolean;
    initializedYn: boolean;
    needsSavingYn: boolean;
    onNoteUpdated: (note: string) => void;
    noteFinalizedYn?: boolean;
    roomIdStr: string;
    sendNoteEnabledYn: boolean;
}
const ERROR_TOAST_ID = 'sent-note-error';
function useInitializeNotes(): NotesContext {
    const detailSession = useDetailSession();
    const { companySettingsStore, uiStore, userStore } = useGlobalStore();
    const [observableNote] = useObservable(detailSession.note.observableNote);
    const [noteFinalizedYn] = useObservable(detailSession.note.noteFinalizedYn);
    const [loadedYn] = useObservable(detailSession.note.loadedYn);
    const [needsSavingYn] = useObservable(detailSession.note.needsSavingYn);
    const [initializedYn = false] = useObservable(detailSession.note.initializedYn);

    const roomNoteCharLimit = companySettingsStore.roomNoteCharLimit;
    const sendNoteEnabledYn = companySettingsStore.sendNoteEnabled;

    const saveMarkdown = useCallback(
        async (markdown: string) => {
            await detailSession.note.saveRoomNote(markdown);
        },
        [detailSession.note],
    );

    const sendNote = useCallback(
        async (markdown: string) => {
            try {
                uiStore.removeToast(ERROR_TOAST_ID);
                await detailSession.note.finalizeRoomNote(markdown);
            } catch (error) {
                uiStore.addToastWithManualClose({
                    id: ERROR_TOAST_ID,
                    flavor: ToastTypes.ERROR,
                    content: (
                        <FailedToSendNoteToastContent tryAgainHandler={() => sendNote(markdown)} />
                    ),
                    manualClose: true,
                });
            }
        },
        [uiStore, detailSession.note],
    );

    useEffect(() => {
        // we want to ensure a note is saved before an expert enters a new call if changes were made
        const dispose = reaction(
            // We do not care about the value of callsAnswered, just that it has been updated,
            // indicating the expert just answered a call.
            () => userStore.callsAnswered,
            () => handleCallAnswered(detailSession.roomId.toString(), detailSession, saveMarkdown),
        );

        // clean up the reaction on unmount
        return dispose;
        // this sets up a reaction--mobx will call the function when observables change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return useMemo(
        () => ({
            currentlySavedMarkdown: observableNote?.text ?? '',
            noteFinalizedYn,
            needsSavingYn,
            loadedYn,
            onNoteUpdated: detailSession.note.onNoteUpdated,
            roomNoteCharLimit,
            saveMarkdown,
            sendNote,
            currentInputText: detailSession.note.currentInputText,
            initializedYn,
            roomIdStr: detailSession.roomId.toString(),
            sendNoteEnabledYn,
        }),
        [
            observableNote?.text,
            noteFinalizedYn,
            needsSavingYn,
            loadedYn,
            detailSession.note.onNoteUpdated,
            detailSession.note.currentInputText,
            detailSession.roomId,
            roomNoteCharLimit,
            saveMarkdown,
            sendNote,
            initializedYn,
            sendNoteEnabledYn,
        ],
    );
}

const [InternalNotesProvider, useNotes] = createContextFactory<NotesContext>('notes2.0');

export function NotesProvider({ children }: PropsWithChildren<object>): JSX.Element {
    const notesData = useInitializeNotes();

    return <InternalNotesProvider value={notesData}>{children}</InternalNotesProvider>;
}

export { useNotes };
